import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { NotifService } from '../../../services/notifs/notif.service';
import { NotifType } from '../../models/notif.model';
import { NotifsUtils } from '../../../utils/notifs/notifs.utils';
import { HttpUrlEncodingCodec } from '@angular/common/http';
import { AuthenticationService } from '../../../services/authentication/authentication.service';

type AuthGuardType =
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree;

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {

  public static REDIRECT_URL = 'redirect_url';

  constructor(
      private readonly authenticationService: AuthenticationService,
      private readonly router: Router,
      private readonly notifService: NotifService
  ) {}

  canActivate(
      route: ActivatedRouteSnapshot,
      state: RouterStateSnapshot
  ): AuthGuardType {
    return this.automaticallyRedirectWhenComesFromMail(route)
        || (this.authenticationService.isLoggedIn() ?? this.parseLoginUrl(state.url));
  }

  canLoad(route: Route, segments: UrlSegment[]): AuthGuardType {
    const fullPath = segments.reduce(
        (path, currentSegment) =>
            `${path}/${currentSegment.path}${window.location.search}`,
        ''
    );

    if (!this.authenticationService.isLoggedIn()) {
      this.parseLoginUrl(fullPath);
    }

    return true;
  }

  /**
   *
   * @param redirectUrl Redirects to login with original URL request as params
   */
  private parseLoginUrl(redirectUrl: string): UrlTree {
    const codec = new HttpUrlEncodingCodec();
    const encodedUrl = codec.encodeValue(redirectUrl);
    const redirect = redirectUrl
        ? `/identity/login?${AuthGuard.REDIRECT_URL}=${encodedUrl}`
        : '/identity/login';
    return this.router.parseUrl(redirect);
  }

  private automaticallyRedirectWhenComesFromMail(
      route: ActivatedRouteSnapshot
  ): boolean {
    const notifType = route.queryParamMap.get('notif');
    const redirectId = route.queryParamMap.get('redirect_id');
    const misc = route.queryParamMap.get('misc');
    if (notifType && redirectId) {
      const type = NotifType[notifType as keyof typeof NotifType];
      if (type) {
        const url = NotifsUtils.getUrl(redirectId, type, misc);
        this.notifService.navigate(url);
        return true;
      }
    }
    return false;
  }
}
